// Food API Caching System
// Smart caching for food predictions, meal data, and file uploads with LRU eviction

import { getFoodApiConfig } from "../config/foodApiConfig";
// Cache-specific types (defined locally to avoid import issues)
interface FoodCacheEntry<T> {
  data: T;
  createdAt: number;
  expiresAt: number;
  accessedAt: number;
  accessCount: number;
  ttl: number;
}

interface FoodCacheStats {
  size: number;
  maxSize: number;
  totalAccesses: number;
  expiredEntries: number;
  hitRate: number;
}

// Temporarily using any types - will be resolved when main service is updated
type PredictFoodResponse = any;
type SaveMealResponse = any;

// LRU Cache implementation for food data
class LRUCache<T> {
  private cache = new Map<string, FoodCacheEntry<T>>();
  private maxSize: number;
  private defaultTTL: number;

  constructor(maxSize: number, defaultTTL: number) {
    this.maxSize = maxSize;
    this.defaultTTL = defaultTTL;
  }

  get(key: string): T | null {
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    // Check if expired
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return null;
    }

    // Move to end (most recently used)
    this.cache.delete(key);
    this.cache.set(key, {
      ...entry,
      accessedAt: Date.now(),
      accessCount: entry.accessCount + 1,
    });

    return entry.data;
  }

  set(key: string, data: T, customTTL?: number): void {
    const ttl = customTTL ?? this.defaultTTL;
    const now = Date.now();

    // Remove oldest entries if at capacity
    if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey) {
        this.cache.delete(firstKey);
      }
    }

    this.cache.set(key, {
      data,
      createdAt: now,
      expiresAt: now + ttl,
      accessedAt: now,
      accessCount: 1,
      ttl,
    });
  }

  delete(key: string): boolean {
    return this.cache.delete(key);
  }

  clear(): void {
    this.cache.clear();
  }

  has(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) {
      return false;
    }

    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  size(): number {
    return this.cache.size;
  }

  keys(): string[] {
    return Array.from(this.cache.keys());
  }

  getStats(): FoodCacheStats {
    const now = Date.now();
    let totalAccess = 0;
    let expiredEntries = 0;

    for (const [, entry] of this.cache.entries()) {
      if (now > entry.expiresAt) {
        expiredEntries++;
      }
      totalAccess += entry.accessCount;
    }

    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      totalAccesses: totalAccess,
      expiredEntries,
      hitRate:
        totalAccess > 0 ? (totalAccess - expiredEntries) / totalAccess : 0,
    };
  }

  cleanup(): number {
    const now = Date.now();
    let removedCount = 0;

    for (const [key, entry] of this.cache.entries()) {
      if (now > entry.expiresAt) {
        this.cache.delete(key);
        removedCount++;
      }
    }

    return removedCount;
  }
}

// Food-specific cache instances
class FoodCache {
  private static instance: FoodCache;
  private config = getFoodApiConfig();

  // Separate caches for different data types
  private predictionCache: LRUCache<PredictFoodResponse>;
  private mealCache: LRUCache<SaveMealResponse>;
  private imageUrlCache: LRUCache<string>;
  private uploadCache: LRUCache<{ document_id: string; file_info: any }>;

  private cleanupInterval: NodeJS.Timeout;

  private constructor() {
    const cacheConfig = this.config.CACHE;

    this.predictionCache = new LRUCache<PredictFoodResponse>(
      Math.floor(cacheConfig.MAX_ENTRIES * 0.4), // 40% for predictions
      cacheConfig.FOOD_PREDICTION_TTL
    );

    this.mealCache = new LRUCache<SaveMealResponse>(
      Math.floor(cacheConfig.MAX_ENTRIES * 0.3), // 30% for meals
      cacheConfig.MEAL_DATA_TTL
    );

    this.imageUrlCache = new LRUCache<string>(
      Math.floor(cacheConfig.MAX_ENTRIES * 0.2), // 20% for image URLs
      cacheConfig.IMAGE_URL_TTL
    );

    this.uploadCache = new LRUCache<{ document_id: string; file_info: any }>(
      Math.floor(cacheConfig.MAX_ENTRIES * 0.1), // 10% for uploads
      cacheConfig.FOOD_PREDICTION_TTL // Same as predictions
    );

    // Setup cleanup interval
    this.cleanupInterval = setInterval(() => {
      this.cleanup();
    }, cacheConfig.CLEANUP_INTERVAL);
  }

  static getInstance(): FoodCache {
    if (!FoodCache.instance) {
      FoodCache.instance = new FoodCache();
    }
    return FoodCache.instance;
  }

  // Food prediction caching
  getFoodPrediction(imageHash: string): PredictFoodResponse | null {
    return this.predictionCache.get(this.getPredictionKey(imageHash));
  }

  setFoodPrediction(imageHash: string, prediction: PredictFoodResponse): void {
    this.predictionCache.set(this.getPredictionKey(imageHash), prediction);
  }

  // Meal data caching
  getMealData(mealId: string): SaveMealResponse | null {
    return this.mealCache.get(this.getMealKey(mealId));
  }

  setMealData(mealId: string, mealData: SaveMealResponse): void {
    this.mealCache.set(this.getMealKey(mealId), mealData);
  }

  // Image URL caching
  getImageUrl(fileName: string): string | null {
    return this.imageUrlCache.get(this.getImageKey(fileName));
  }

  setImageUrl(fileName: string, url: string): void {
    this.imageUrlCache.set(this.getImageKey(fileName), url);
  }

  // Upload result caching
  getUploadResult(
    fileHash: string
  ): { document_id: string; file_info: any } | null {
    return this.uploadCache.get(this.getUploadKey(fileHash));
  }

  setUploadResult(
    fileHash: string,
    uploadResult: { document_id: string; file_info: any }
  ): void {
    this.uploadCache.set(this.getUploadKey(fileHash), uploadResult);
  }

  // Cache invalidation methods
  invalidateMeal(mealId: string): void {
    this.mealCache.delete(this.getMealKey(mealId));
  }

  invalidatePrediction(imageHash: string): void {
    this.predictionCache.delete(this.getPredictionKey(imageHash));
  }

  // Clear all caches
  clearAll(): void {
    this.predictionCache.clear();
    this.mealCache.clear();
    this.imageUrlCache.clear();
    this.uploadCache.clear();
  }

  // Clear specific cache types
  clearPredictions(): void {
    this.predictionCache.clear();
  }

  clearMeals(): void {
    this.mealCache.clear();
  }

  clearUploads(): void {
    this.uploadCache.clear();
  }

  // Statistics and monitoring
  getStats(): {
    predictions: FoodCacheStats;
    meals: FoodCacheStats;
    imageUrls: FoodCacheStats;
    uploads: FoodCacheStats;
    total: { size: number; maxSize: number };
  } {
    return {
      predictions: this.predictionCache.getStats(),
      meals: this.mealCache.getStats(),
      imageUrls: this.imageUrlCache.getStats(),
      uploads: this.uploadCache.getStats(),
      total: {
        size:
          this.predictionCache.size() +
          this.mealCache.size() +
          this.imageUrlCache.size() +
          this.uploadCache.size(),
        maxSize: this.config.CACHE.MAX_ENTRIES,
      },
    };
  }

  // Cleanup expired entries
  cleanup(): number {
    const predictionCleaned = this.predictionCache.cleanup();
    const mealCleaned = this.mealCache.cleanup();
    const imageCleaned = this.imageUrlCache.cleanup();
    const uploadCleaned = this.uploadCache.cleanup();

    const totalCleaned =
      predictionCleaned + mealCleaned + imageCleaned + uploadCleaned;

    return totalCleaned;
  }

  // Key generation helpers
  private getPredictionKey(imageHash: string): string {
    return `pred_${imageHash}`;
  }

  private getMealKey(mealId: string): string {
    return `meal_${mealId}`;
  }

  private getImageKey(fileName: string): string {
    return `img_${fileName}`;
  }

  private getUploadKey(fileHash: string): string {
    return `upload_${fileHash}`;
  }

  // Cleanup on destruction
  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
    this.clearAll();
  }
}

// Utility functions for file hashing (for cache keys)
export const generateFileHash = async (file: File): Promise<string> => {
  try {
    // Use file properties for a simple hash
    const content = `${file.name}_${file.size}_${file.lastModified}_${file.type}`;

    // Simple hash function
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32bit integer
    }

    return Math.abs(hash).toString(36);
  } catch (_error) {
    // Fallback to timestamp + random
    return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
};

export const generateImageHash = (imageUrl: string): string => {
  // Extract meaningful parts of image URL for hashing
  try {
    const url = new URL(imageUrl);
    const path = url.pathname + url.search;

    let hash = 0;
    for (let i = 0; i < path.length; i++) {
      const char = path.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }

    return Math.abs(hash).toString(36);
  } catch {
    return imageUrl.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
  }
};

// Singleton export
export const FoodCacheManager = FoodCache.getInstance();
